import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useSocket } from './SocketProvider';
import CallInterface from '../module/CallInterface/CallInterface';

const CallContext = createContext(null);

export function CallProvider() {
    const socket = useSocket();

    const [activeCall, setActiveCall] = useState(null);
    // activeCall = { targetUser, isIncomingCall, incomingOffer } | null

    const pendingOfferRef = useRef(null);

    useEffect(() => {
        if (!socket) return;

        const handleIncomingCall = ({ fromUserId, callerInfo }) => {
            // НЕ обнуляем pendingOfferRef: из-за await в обработчике call:start на сервере
            // call:offer может прийти РАНЬШЕ call:incoming, и оффер уже здесь —
            // иначе приём звонка зависнет (acceptCall не дождётся оффера).
            // callerInfo приходит с сервера (имя/фамилия/аватар звонящего из БД).
            const caller = {
                firstName: callerInfo?.firstName,
                lastName: callerInfo?.lastName,
                avatar: callerInfo?.avatar,
            };
            setActiveCall(prev => {
                if (prev && String(prev.targetUser.id) === String(fromUserId)) {
                    // call:offer мог прийти раньше и создать targetUser только с id —
                    // дополняем его данными звонящего, ничего не затирая.
                    return {
                        ...prev,
                        targetUser: {
                            ...prev.targetUser,
                            firstName: caller.firstName ?? prev.targetUser.firstName,
                            lastName: caller.lastName ?? prev.targetUser.lastName,
                            avatar: caller.avatar ?? prev.targetUser.avatar,
                        },
                    };
                }
                return {
                    targetUser: { id: fromUserId, ...caller },
                    isIncomingCall: true,
                    incomingOffer: pendingOfferRef.current ?? null,
                };
            });
        };

        const handleOffer = ({ fromUserId, offer }) => {
            // console.log('Global: call:offer from', fromUserId);
            pendingOfferRef.current = offer;
            setActiveCall(prev => {
                // Если звонок уже открыт — просто обновляем offer
                if (prev && String(prev.targetUser.id) === String(fromUserId)) {
                    return { ...prev, incomingOffer: offer };
                }
                // Если offer пришёл раньше call:incoming
                return {
                    targetUser: { id: fromUserId },
                    isIncomingCall: true,
                    incomingOffer: offer,
                };
            });
        };

        const handleEnd = () => {
            pendingOfferRef.current = null;
            setActiveCall(null);
        };

        // Звонок приняли или отклонили на ДРУГОМ устройстве этого же аккаунта
        // (телефон, вторая вкладка) — здесь он больше не звонит.
        //
        // Устройство, где ответили, это событие не получает: сервер шлёт его в
        // комнату пользователя через сокет-инициатор, а тот сам себе рассылку не
        // доставляет. byDeviceId остаётся для пушей и второго сокета того же
        // устройства — веб его не заводит, поэтому здесь просто закрываем звонок.
        const handleCancel = () => {
            pendingOfferRef.current = null;
            setActiveCall(null);
        };

        // Звонок отклонён сервером (блок / приватность) — закрываем исходящий вызов
        const handleDenied = () => {
            pendingOfferRef.current = null;
            setActiveCall(null);
        };

        const handleReconnect = ({ fromUserId }) => {
            pendingOfferRef.current = null;
            setActiveCall({
                targetUser: { id: fromUserId },
                isIncomingCall: true,
                isReconnect: true,
                incomingOffer: null,
            });
        };

        socket.on('call:incoming', handleIncomingCall);
        socket.on('call:offer', handleOffer);
        socket.on('call:end', handleEnd);
        socket.on('call:cancel', handleCancel);
        socket.on('call:denied', handleDenied);
        socket.on('call:reconnect', handleReconnect);

        return () => {
            socket.off('call:incoming', handleIncomingCall);
            socket.off('call:offer', handleOffer);
            socket.off('call:end', handleEnd);
            socket.off('call:cancel', handleCancel);
            socket.off('call:denied', handleDenied);
            socket.off('call:reconnect', handleReconnect);
        };
    }, [socket]);

    const startCall = (targetUser) => {
        setActiveCall({
            targetUser,
            isIncomingCall: false,
            incomingOffer: null,
        });
    };

    const closeCall = () => {
        pendingOfferRef.current = null;
        setActiveCall(null);
    };

    return (
        <CallContext.Provider value={{ startCall }}>
            <Outlet />

            {activeCall && (
                <CallInterface
                    socket={socket}
                    targetUser={activeCall.targetUser}
                    isIncomingCall={activeCall.isIncomingCall}
                    incomingOfferRef={pendingOfferRef}
                    isReconnect={activeCall.isReconnect ?? false}
                    onClose={closeCall}
                />
            )}
        </CallContext.Provider>
    );
}

export const useCall = () => useContext(CallContext);
